import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Grid, Paper, Select, MenuItem, 
  FormControl, InputLabel, 
  Tabs, Tab, Container, useTheme, useMediaQuery,
  Avatar
} from '@mui/material';
import { 
  Warning as WarningIcon, 
  CheckCircle as MitigatedIcon, 
  Error as OccurredIcon, 
  Visibility as IdentifiedIcon,
  DoneAll as ClosedIcon
} from '@mui/icons-material';
import { RiskStats } from '../types/risk';
import riskService from '../api/riskService';
import RiskMatrix from '../components/RiskMatrixComponent';
import RiskList from '../components/RiskList';

const RiskManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [projectId, setProjectId] = useState<number | undefined>(undefined);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState<RiskStats>({
    total: 0,
    identified: 0,
    mitigated: 0,
    occurred: 0,
    closed: 0
  });

  const fetchRiskStats = useCallback(async () => {
    try {
      const riskStats = await riskService.getRiskStats(projectId);
      setStats(riskStats);
    } catch (error) {
      console.error('Failed to fetch risk stats:', error);
    }
  }, [projectId]);

  useEffect(() => {
    fetchRiskStats();
  }, [fetchRiskStats]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const statCards = [
    { title: 'Total Risks', value: stats.total, icon: <WarningIcon fontSize="small" />, color: '#64748b' },
    { title: 'Identified', value: stats.identified, icon: <IdentifiedIcon fontSize="small" />, color: theme.palette.primary.main },
    { title: 'Mitigated', value: stats.mitigated, icon: <MitigatedIcon fontSize="small" />, color: '#10b981' },
    { title: 'Occurred', value: stats.occurred, icon: <OccurredIcon fontSize="small" />, color: '#ef4444' },
    { title: 'Closed', value: stats.closed, icon: <ClosedIcon fontSize="small" />, color: '#94a3b8' }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 6, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 3 }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
            Zandrigo Risks
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Identify, assess, and track project threats
          </Typography>
        </Box>
        
        <FormControl sx={{ minWidth: 250 }} size="medium">
          <InputLabel>Filter by Project</InputLabel>
          <Select
            value={projectId || ''}
            label="Filter by Project"
            onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : undefined)}
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="">All Active Projects</MenuItem>
            <MenuItem value={1}>Project 1</MenuItem>
            <MenuItem value={2}>Project 2</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {statCards.map((card, index) => (
          <Grid {...{ item: true, xs: 6, sm: 4, md: 2.4 }} key={index}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider', height: '100%', textAlign: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 48, height: 48, bgcolor: `${card.color}15`, color: card.color }}>
                  {React.cloneElement(card.icon as React.ReactElement, { sx: { fontSize: 24 } } as any)}
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {card.title}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Tabs for Content */}
      <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 2 }}>
            <Tab label="Risk Matrix" sx={{ fontWeight: 700, py: 2 }} />
            <Tab label="Risk List" sx={{ fontWeight: 700, py: 2 }} />
          </Tabs>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          {tabValue === 0 && (
            <Box sx={{ overflowX: 'auto', py: 2 }}>
              <RiskMatrix projectId={projectId} />
            </Box>
          )}
          {tabValue === 1 && (
            <RiskList projectId={projectId} />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default RiskManagementPage;